// src/services/supabase/bids.js
// Servicios para gestión de ofertas (bids)

import { supabase } from '../../supabaseClient';

/**
 * Obtiene todas las ofertas activas (OPEN + LAST_CHANCE) de todas las propiedades
 * @returns {Promise<Array>} Lista de ofertas ordenadas por tiempo descendente
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getBids = async () => {
  try {
    const { data, error } = await supabase
      .from('bids')
      .select('*')
      .eq('is_active', true)
      .order('bid_time', { ascending: false });

    if (error) throw error;
    
    return data.map(b => ({
      id: b.id,
      propertyId: b.property_id,
      buyerName: b.buyer_name,
      buyerEmail: b.buyer_email,
      amount: parseFloat(b.amount),
      timestamp: b.bid_time,
      conditionsText: b.conditions_text,
      is_last_chance: b.is_last_chance || false,
    }));
  } catch (error) {
    console.error('Error obteniendo ofertas:', error);
    throw error;
  }
};

/**
 * Obtiene todas las ofertas de una propiedad específica con condiciones incluidas
 * Usa función RPC de Supabase para join eficiente
 * @param {string} propertyId - UUID de la propiedad
 * @returns {Promise<Array>} Lista de ofertas con condiciones seleccionadas
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getPropertyBids = async (propertyId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_property_bids_with_conditions', {
        p_property_id: propertyId,
      });

    if (error) throw error;
    
    return data.map(b => ({
      id: b.bid_id,
      propertyId: b.property_id,
      buyerName: b.buyer_name,
      buyerEmail: b.buyer_email,
      amount: parseFloat(b.amount),
      timestamp: b.bid_time,
      conditionsText: b.conditions_text,
      selectedConditions: b.selected_conditions || [],
      hasConditions: b.has_conditions,
      is_last_chance: b.is_last_chance || false,
    })).sort((a, b) => b.amount - a.amount || new Date(a.timestamp) - new Date(b.timestamp));
  } catch (error) {
    console.error('Error obteniendo ofertas de propiedad:', error);
    throw error;
  }
};

/**
 * Obtiene una oferta específica con sus condiciones asociadas
 * @param {string} bidId - UUID de la oferta
 * @returns {Promise<Object|null>} Oferta con condiciones o null si no existe
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getBidWithConditions = async (bidId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_bid_with_conditions', {
        p_bid_id: bidId,
      });

    if (error) throw error;
    
    if (!data || data.length === 0) return null;
    
    const b = data[0];
    return {
      id: b.bid_id,
      propertyId: b.property_id,
      buyerName: b.buyer_name,
      buyerEmail: b.buyer_email,
      amount: parseFloat(b.amount),
      timestamp: b.bid_time,
      conditionsText: b.conditions_text,
      selectedConditions: b.selected_conditions || [],
      is_last_chance: b.is_last_chance || false,
    };
  } catch (error) {
    console.error('Error obteniendo oferta con condiciones:', error);
    throw error;
  }
};

/**
 * Valida y crea una nueva oferta con condiciones opcionales
 * Valida monto contra reglas de propiedad (excepto en LAST_CHANCE)
 * @param {Object} bidData - Datos de la oferta
 * @param {string} bidData.propertyId - UUID de la propiedad
 * @param {string} bidData.buyerName - Nombre del comprador
 * @param {string} bidData.buyerEmail - Email del comprador
 * @param {number} bidData.amount - Monto de la oferta
 * @param {string} [bidData.conditionsText] - Texto de condiciones adicionales
 * @param {boolean} [bidData.is_last_chance=false] - Si es oferta de última oportunidad
 * @param {Array<string>} [bidData.selectedConditionIds=[]] - IDs de condiciones seleccionadas
 * @returns {Promise<Object>} Resultado con success y bid o error con validación
 * @throws {Error} Si hay error al crear en Supabase
 */
export const createBid = async (bidData) => {
  try {
    // Primero validar la oferta usando la función de Supabase
    // Saltar validación de Supabase si es última oportunidad
    // La validación ya se hizo en el frontend con validateLastChanceBid()
    if (!bidData.is_last_chance) {
      // Solo validar con Supabase en periodo normal
      const { data: validation, error: validationError } = await supabase
        .rpc('validate_bid', {
          p_property_id: bidData.propertyId,
          p_amount: bidData.amount,
        });

      if (validationError) throw validationError;
      
      const validationResult = validation[0];
      
      if (!validationResult.is_valid) {
        return {
          success: false,
          error: validationResult.error_message,
          currentPrice: parseFloat(validationResult.current_price),
          minRequired: parseFloat(validationResult.min_required),
          maxAllowed: parseFloat(validationResult.max_allowed),
        };
      }
    }

    // Si es válida, insertar la oferta
    const { data: bidResult, error: bidError } = await supabase
      .from('bids')
      .insert([
        {
          property_id: bidData.propertyId,
          buyer_name: bidData.buyerName,
          buyer_email: bidData.buyerEmail,
          amount: bidData.amount,
          conditions_text: bidData.conditionsText || null,
          is_last_chance: bidData.is_last_chance || false,
        },
      ])
      .select()
      .single();

    if (bidError) throw bidError;
    
    // Si hay condiciones seleccionadas, insertarlas
    const conditionIds = Array.isArray(bidData.selectedConditionIds) ? bidData.selectedConditionIds : [];
    if (conditionIds.length > 0) {
      const conditionsToInsert = conditionIds.map(conditionId => ({
        bid_id: bidResult.id,
        condition_id: conditionId,
      }));
      
      const { error: conditionsError } = await supabase
        .from('bid_selected_conditions')
        .insert(conditionsToInsert);
      
      if (conditionsError) {
        console.error('Error guardando condiciones:', conditionsError);
        // No fallar si hay error en condiciones, la oferta ya está guardada
      }
    }
    
    return {
      success: true,
      bid: {
        id: bidResult.id,
        propertyId: bidResult.property_id,
        buyerName: bidResult.buyer_name,
        buyerEmail: bidResult.buyer_email,
        amount: parseFloat(bidResult.amount),
        timestamp: bidResult.bid_time,
        conditionsText: bidResult.conditions_text,
        selectedConditionIds: bidData.selectedConditionIds || [],
        is_last_chance: bidResult.is_last_chance || false,
      },
    };
  } catch (error) {
    console.error('Error creando oferta:', error);
    throw error;
  }
};

/**
 * Obtiene todas las condiciones de ofertas disponibles (catálogo)
 * @returns {Promise<Array>} Lista de condiciones ordenadas por posición
 * @throws {Error} Si hay error en la consulta a Supabase
 */
export const getBidConditions = async () => {
  try {
    const { data, error } = await supabase
      .from('bid_conditions')
      .select('*')
      .eq('is_active', true)
      .order('order_position', { ascending: true });

    if (error) throw error;
    
    return data.map(c => ({
      id: c.id,
      label: c.label,
      icon: c.icon,
      orderPosition: c.order_position,
    }));
  } catch (error) {
    console.error('Error obteniendo condiciones:', error);
    throw error;
  }
};